import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { NO_ACCESS, VIEWER } from '@/constants/team';
import { captureException } from '@/errors';
import {
  Contact,
  CopilotContactsRecordContext,
  PartnerContact,
} from '@/types/copilotContacts';
import {
  CopilotFetchedAccount,
  GroupedOverlap,
  GroupedOverlaps,
  Overlap,
  OverlapResult,
} from '@/types/sales';
import { Source } from '@/types/sources';
import urls from '@/urls';
import { returnTrimmedDomain } from '@/utils';

import { useFeedsStore } from './FeedsStore';
import { usePartnersStore } from './PartnersStore';
import { useSourcesStore } from './SourcesStore';
import { initStore } from './store-utils';

type ApiResult = { data?: { data?: unknown }; error?: unknown };

export const useCopilotContactsStore = defineStore(
  'CopilotContactsStore',
  () => {
    const contacts = ref<Contact[]>([]);
    const overlaps = ref<GroupedOverlaps>({});
    const messageReadyResults = ref(null);
    const messageReadyResultsForOverlaps = ref<Record<string, OverlapResult>>(
      {},
    );
    const currentRecordId = ref<string | null>(null);

    const { currentAuth } = useAuth();
    const isLimitedUser = computed(() =>
      [VIEWER, null, NO_ACCESS].includes(
        currentAuth.value?.sales_edge_role ?? null,
      ),
    );

    const recordId = ref<string | null>(null);
    const greenfieldId = ref<string | null>(null);
    const sourceId = ref<number | null>(null);

    const cacheKey = computed(
      () => greenfieldId.value ?? recordId.value ?? null,
    );

    const { initError, ready, readySync, running, refresh } = initStore(
      async () => {
        if (!cacheKey.value || currentRecordId.value === cacheKey.value) return;
        const sourcesStore = useSourcesStore();
        const feedsStore = useFeedsStore();
        const effectiveGreenfieldId = greenfieldId.value;
        const effectiveRecordId = recordId.value;
        if (!effectiveGreenfieldId && !effectiveRecordId) return;
        let contactsPromise: Promise<ApiResult>;
        let overlapsPromise: Promise<ApiResult>;

        if (effectiveGreenfieldId) {
          contactsPromise = crossbeamApi.GET(
            '/v0.1/records/deal-nav/lead-generation/greenfield/{greenfield_uuid}/partner-contacts',
            {
              params: {
                path: { greenfield_uuid: effectiveGreenfieldId },
              },
            },
          );
          overlapsPromise = crossbeamApi.GET(
            '/v0.1/records/deal-nav/lead-generation/greenfield/{greenfield_uuid}/partner-overlaps',
            {
              params: {
                path: { greenfield_uuid: effectiveGreenfieldId },
              },
            },
          );
        } else {
          if (!effectiveRecordId) return;
          contactsPromise = crossbeamApi.GET(
            '/v0.1/records/{crm_record_id}/partner-contacts',
            {
              params: {
                path: { crm_record_id: effectiveRecordId },
                query: {
                  use_copilot_config: false,
                  ...(sourceId.value != null && {
                    source_id: sourceId.value,
                  }),
                },
              },
            },
          );
          overlapsPromise = crossbeamApi.GET('/v1/overlaps/accounts/search', {
            params: {
              query: {
                record_id: effectiveRecordId,
                use_copilot_config: false,
                ...(sourceId.value != null && {
                  source_id: sourceId.value,
                }),
              },
            },
          });
        }

        const [
          { data: contactsResponse, error: contactsError },
          { data: overlapsResponse, error: overlapsError },
        ] = await Promise.all([
          contactsPromise,
          overlapsPromise,
          sourcesStore.refreshSourcesStore(),
          feedsStore.refreshFeedsStore(),
        ]);
        if (contactsError || overlapsError)
          throw new Error(
            `Failed to load partner contacts for ${cacheKey.value}`,
          );
        contacts.value =
          (contactsResponse?.data as Contact[] | undefined) ?? [];
        const overlapsData = (overlapsResponse?.data ?? []) as Overlap[];
        overlaps.value = overlapsData.reduce(
          (acc: GroupedOverlaps, overlap: Overlap) => {
            const {
              partner_id,
              partner_name,
              partner_logo_url,
              record_id,
              record_website,
              record_name,
            } = overlap;
            if (!acc[partner_id]) {
              acc[partner_id] = {
                accounts: [],
                partner_name,
                partner_logo_url,
                partner_id,
                record_id,
                record_website,
                record_name,
              } as GroupedOverlap;
            }
            acc[partner_id].accounts.push({
              ...overlap,
            });
            return acc;
          },
          {} as GroupedOverlaps,
        );
        if (!isLimitedUser.value) {
          await fetchMessageReadyResults();
        }
        currentRecordId.value = cacheKey.value;
      },
    );

    function setRecordContext(context: CopilotContactsRecordContext) {
      const nextGreenfieldId = context.greenfieldId ?? null;
      const nextRecordId = context.recordId ?? null;
      if ((nextGreenfieldId ?? nextRecordId) !== cacheKey.value)
        clearRecordData();
      greenfieldId.value = nextGreenfieldId;
      recordId.value = nextRecordId;
      sourceId.value = context.sourceId ?? null;
      initError.value = null;
      return refresh();
    }

    function refreshCopilotContactsStore() {
      currentRecordId.value = null;
      return refresh();
    }

    function clearRecordData() {
      contacts.value = [];
      overlaps.value = {};
      messageReadyResults.value = null;
      messageReadyResultsForOverlaps.value = {};
      currentRecordId.value = null;
    }

    // Getters

    const account = computed(() => {
      if (!Object.values(overlaps.value).length) return {};
      const firstItem: undefined | GroupedOverlap | Overlap = Object.values(
        overlaps.value,
      )[0];
      if (!firstItem) return null;
      return {
        id: firstItem.record_id,
        name: firstItem.record_name,
        website: firstItem.record_website,
        domain: returnTrimmedDomain(firstItem.record_website),
      } as CopilotFetchedAccount;
    });

    const contactsGroupedByEmail = computed(() =>
      computeContactsGroupedByEmail(contacts.value),
    );

    function contactsGroupedByEmailForOverlap(partnerId: string) {
      if (!contacts.value) return [];
      const emails = contacts.value
        .filter((item) => item.partner_id === partnerId)
        .map((item) => item.partner_contact_email?.toLowerCase());
      return computeContactsGroupedByEmail(
        contacts.value.filter((item) =>
          emails.includes(item.partner_contact_email?.toLowerCase()),
        ),
      );
    }

    function getContactByEmail(email: string) {
      return contactsGroupedByEmail.value.find(
        (item: PartnerContact) => item.email === email,
      );
    }

    function getOverlapByPartnerId(partnerId: string) {
      if (!Array.isArray(overlaps.value)) return;
      return overlaps.value.find(
        (item: Overlap) => item.partner_id === partnerId,
      );
    }

    function getOverlapsByPartnerId(partnerId: string) {
      if (Array.isArray(overlaps.value)) return;
      return overlaps.value[partnerId];
    }

    function getOverlapByPartnerCrossbeamRecordId(
      partnerCrossbeamRecordId: string,
    ) {
      if (!Array.isArray(overlaps.value)) return;
      return overlaps.value.find(
        (item) => item.partner_crossbeam_record_id === partnerCrossbeamRecordId,
      );
    }

    const contactsNotSynced = computed(() => {
      const sourcesStore = useSourcesStore();
      const contactSources = sourcesStore.sources;
      if (!contactSources.length) {
        return false;
      }
      return (
        contactSources.length &&
        !contactSources?.some((source: Source) => source.sync_enabled)
      );
    });

    const getCRMUrl = computed(() => {
      const feedsStore = useFeedsStore();
      if (feedsStore.hasMultipleCRMs || !feedsStore.crmFeeds.length) {
        return '';
      }
      return feedsStore.crmFeeds[0]?.external_base_url;
    });

    const getContactsSources = computed(() => {
      const sourcesStore = useSourcesStore();
      return sourcesStore.sources.filter(
        ({ mdm_type: type, sync_enabled: enabled }) =>
          type === 'contact' && enabled,
      );
    });

    function getOverlapMessageReadyResult(overlap: Overlap | GroupedOverlap) {
      return overlap
        ? messageReadyResultsForOverlaps.value[
            `${overlap.crossbeam_record_id}-${overlap.partner_crossbeam_record_id}`
          ]
        : null;
    }

    // Helpers
    async function fetchMessageReadyResults() {
      const partnersStore = usePartnersStore();
      await partnersStore.readySync;
      try {
        const payload = Object.values(overlaps.value).flatMap(
          (overlapGroup: GroupedOverlap) =>
            overlapGroup.accounts.map((overlap: Overlap) => ({
              XbRecordId: overlap.crossbeam_record_id,
              XbPartnerOrgId: partnersStore.getPartnerOrgByUuid(
                overlap.partner_id,
              )?.id,
              XbPartnerRecordId: overlap.partner_crossbeam_record_id,
            })),
        );
        const { data } = await axios.post(
          urls.salesEdge.post.messageReadyV2,
          payload,
        );
        messageReadyResults.value = data;
        data.overlaps.forEach((overlapResult: OverlapResult) => {
          messageReadyResultsForOverlaps.value[
            `${overlapResult.XbRecordId}-${overlapResult.XbPartnerRecordId}`
          ] = overlapResult;
        });
      } catch (err) {
        captureException(err);
      }
    }

    function getContactsByPartnerId(partnerId: string) {
      return contacts.value.filter(
        (contact) => contact.partner_id === partnerId,
      );
    }

    function computeContactsGroupedByEmail(contacts: Contact[]) {
      return contacts.reduce((acc: PartnerContact[], contact) => {
        const email = contact.partner_contact_email?.toLowerCase();
        if (!email) {
          return acc;
        }
        const existing = acc.find((item) => item.email === email);
        const nameSplit = contact.partner_contact_name?.split(' ');
        const contactParsed = {
          ...contact,
          first_name: (!!nameSplit?.length && nameSplit[0]) || '',
          last_name:
            (!!nameSplit?.length && nameSplit.slice(1).join(' ')) || '',
          overlap: getOverlapsByPartnerId(contact.partner_id)?.accounts.find(
            (account) =>
              account.partner_crossbeam_record_id ===
              contact.partner_crossbeam_record_id,
          ),
        };

        if (existing) {
          existing.contacts.push(contactParsed);
        } else {
          acc.push({
            email,
            first_contact: contactParsed,
            contacts: [contactParsed],
          });
        }
        return acc;
      }, []);
    }

    return {
      account,
      contacts,
      contactsGroupedByEmail,
      contactsGroupedByEmailForOverlap,
      contactsNotSynced,
      getContactByEmail,
      getContactsSources,
      getContactsByPartnerId,
      getCRMUrl,
      getOverlapByPartnerId,
      getOverlapsByPartnerId,
      getOverlapByPartnerCrossbeamRecordId,
      getOverlapMessageReadyResult,
      messageReadyResults,
      messageReadyResultsForOverlaps,
      overlaps,
      currentRecordId,
      recordId,
      greenfieldId,
      sourceId,
      setRecordContext,
      ready,
      readySync,
      error: initError,
      running,
      refreshCopilotContactsStore,
    };
  },
);
